import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../DataContext';
import { useLang } from '../LanguageContext'; // ★ 언어 훅
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, RotateCcw, Search, Calculator, ChevronLeft, ChevronRight } from 'lucide-react';

const TransactionHistory = () => {
  const { transactions, auditLogs, agentTransactions, games, currentUser } = useData();
  const { t } = useLang(); // ★ 번역 함수
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('game'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ★ [추가] 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const fmt = (num) => num ? Math.floor(Number(num)).toLocaleString() : '0';

  // ★ [수정] 관리자 권한 체크 로직 강화
  const userType = currentUser?.type || '';
  const isAdmin = 
      userType === 'Master_Admin' || 
      userType === 'Staff' || 
      userType.includes('Manager');

  // --- 에이전트 클릭 핸들러 ---
  const handleAgentClick = (agentCode) => {
      navigate('/agents', { state: { targetAgentCode: agentCode } });
  };

  // ★ [추가] 필터나 탭이 변경되면 페이지를 1로 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, startDate, endDate]);

  const isWithinDateRange = (targetDateStr) => {
      if (!targetDateStr) return false;
      const targetTime = new Date(targetDateStr).getTime();
      if (startDate) {
          const startTime = new Date(startDate).getTime();
          if (targetTime < startTime) return false;
      }
      if (endDate) {
          const endTime = new Date(endDate).getTime();
          if (targetTime > endTime) return false;
      }
      return true;
  };

  const matchesSearch = (text) => {
      if (!searchTerm) return true;
      return (text || '').toLowerCase().includes(searchTerm.toLowerCase());
  };

  const renderRemark = (tx) => {
      if ((tx.type === 'Chips Out' || tx.type === 'Cash-out' || tx.type === 'Settle' || tx.type === 'Return') && tx.amount) {
          const total = parseFloat(tx.amount);
          const roll = parseFloat(tx.rollingChips || 0);
          const cash = total - roll;
          
          if (roll > 0 || cash > 0) {
              return (
                  <div style={{ textAlign: 'left' }}>
                      {tx.remark && <div style={{ marginBottom: '4px' }}>{tx.remark}</div>}
                      <div style={{ fontSize: '11px', color: '#64748b', backgroundColor: '#f1f5f9', padding: '4px', borderRadius: '4px', display:'inline-block' }}>
                          <span style={{ color: '#0f172a', fontWeight:'bold' }}>Cash: {fmt(cash)}</span>
                          <span style={{ margin: '0 5px', color: '#cbd5e1' }}>|</span>
                          <span style={{ color: '#d97706', fontWeight:'bold' }}>Roll: {fmt(roll)}</span>
                      </div>
                  </div>
              );
          }
      }
      return tx.remark;
  };

  const formatLogType = (type) => {
      const map = {
          '회원 수정': 'Agent Update',
          '회원 생성': 'Agent Create',
          '게임 생성': 'Game Create',
          '입금': 'Deposit',
          '출금': 'Withdraw'
      };
      return map[type] || type;
  };

  const parseDetailText = (text) => {
      if (!text) return '';
      let str = text;
      str = str.replace(/신규 에이전트 등록 완료/g, 'New Agent Registered');
      str = str.replace(/텔레그램ID/g, 'Telegram ID');
      str = str.replace(/전화번호/g, 'Phone');
      str = str.replace(/기본 롤링/g, 'Rolling Rate');
      str = str.replace(/정보 업데이트/g, 'Info Updated');
      str = str.replace(/변경/g, 'Changed');
      return str;
  };

  const getChangeDetails = (log) => {
    let rawStr = '';
    if (log.changeLog && typeof log.changeLog === 'string' && log.changeLog.length > 5) rawStr = log.changeLog;
    else if (log.changes && typeof log.changes === 'string' && log.changes.length > 5) rawStr = log.changes;
    else if (log.detail && typeof log.detail === 'string' && log.detail !== '정보 업데이트' && log.detail.length > 2) rawStr = log.detail;

    if (rawStr) {
        return parseDetailText(rawStr);
    }
    
    if (log.changes && typeof log.changes === 'object' && Object.keys(log.changes).length > 0) {
      const details = [];
      const fieldMap = {
        phone: 'Phone', nationality: 'Nationality', name: 'Name',
        engSurname: 'Surname', engGivenName: 'Given Name', nativeName: 'Native Name',
        credit: 'Credit', status: 'Status', memo: 'Memo', remark: 'Remark',
        bankName: 'Bank Name', accountNumber: 'Account No',
        rate: 'Rate', password: 'Password', upline: 'Upline', type: 'Type'
      };

      Object.keys(log.changes).forEach(key => {
        const change = log.changes[key];
        const label = fieldMap[key] || key;
        
        if (change && typeof change === 'object' && ('old' in change || 'new' in change)) {
          const oldValue = change.old || '(Null)';
          const newValue = change.new || '(Null)';
          if (String(oldValue).trim() !== String(newValue).trim()) {
            details.push(`${label}: ${oldValue} → ${newValue}`);
          }
        } else if (typeof change === 'string' || typeof change === 'number') {
             details.push(`${label}: ${change}`);
        }
      });
      if (details.length > 0) return details.join(', ');
    }

    return 'Info Updated';
  };

  // --- 데이터 필터링 ---
  const filteredGameTxs = Object.values(transactions).flat()
    .sort((a,b) => new Date(b.date) - new Date(a.date))
    .filter(tx => {
        if (!isAdmin && tx.agentCode !== currentUser.code) return false;
        if (!isWithinDateRange(tx.date)) return false;
        const game = games.find(g => g.id === tx.gameId);
        const agentId = game ? game.agentId : '';
        return matchesSearch(tx.gameId) || matchesSearch(agentId) || matchesSearch(tx.type) || matchesSearch(tx.remark) || matchesSearch(tx.staff);
    });

  const filteredAccountTxs = agentTransactions
    .sort((a,b) => new Date(b.date) - new Date(a.date))
    .filter(tx => {
        if (!isAdmin && tx.agentCode !== currentUser.code) return false;
        if (!isWithinDateRange(tx.date)) return false;
        return matchesSearch(tx.agentCode) || matchesSearch(tx.gameId) || matchesSearch(tx.type) || matchesSearch(tx.remark);
    });

  const filteredAuditLogs = auditLogs
    .sort((a,b) => new Date(b.date) - new Date(a.date))
    .filter(log => {
        if (!isWithinDateRange(log.date)) return false;
        const detailText = getChangeDetails(log);
        return matchesSearch(log.targetId) || matchesSearch(log.type) || matchesSearch(detailText) || matchesSearch(log.staff);
    });

  const casinoChipsTxs = filteredAccountTxs.filter(tx => 
      ['Buy-in', 'Initial Buy-in', 'Add Buy-in', 'Cash-out', 'Chips Out', 'Return'].includes(tx.type)
  );

  const casinoCashTxs = filteredAccountTxs
    .filter(tx => ['Deposit', 'Withdraw', 'Rolling Commission', 'Share Profit', 'Rebate'].includes(tx.type))
    .map(tx => ({ ...tx, flow: tx.type==='Deposit'?'OUT (To Casino)':'IN (From Casino)' }));

  const commissionTxs = filteredAccountTxs.filter(tx => tx.type === 'Rolling Commission');
  const shareProfitTxs = filteredAccountTxs.filter(tx => tx.type === 'Share Profit');
  const rebateTxs = filteredAccountTxs.filter(tx => tx.type === 'Rebate');

  const commissionTotal = useMemo(() => commissionTxs.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0), [commissionTxs]);
  const shareProfitTotal = useMemo(() => shareProfitTxs.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0), [shareProfitTxs]);
  const rebateTotal = useMemo(() => rebateTxs.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0), [rebateTxs]);

  // ★ [추가] 현재 탭에 맞는 데이터 선택 및 페이지네이션
  const getActiveData = () => {
    switch (activeTab) {
        case 'game': return filteredGameTxs;
        case 'audit': return filteredAuditLogs;
        case 'account': return filteredAccountTxs;
        case 'casino_chips': return casinoChipsTxs;
        case 'casino_cash': return casinoCashTxs;
        case 'commission': return commissionTxs;
        case 'share_profit': return shareProfitTxs;
        case 'rebate': return rebateTxs;
        default: return [];
    }
  };

  const activeData = getActiveData();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = activeData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(activeData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
    }
  };

  const renderTypeBadge = (type) => {
      let bg = '#f3f4f6'; let color = '#374151'; let border = '1px solid #d1d5db';
      if (['Deposit', 'Buy-in', 'Initial Buy-in', 'Add Buy-in', 'Return', 'Game Win'].includes(type)) {
          bg = '#f0fdf4'; color = '#15803d'; border = '1px solid #bbf7d0';
      } else if (['Withdraw', 'Cash-out', 'Chips Out', 'Game Loss'].includes(type)) {
          bg = '#fef2f2'; color = '#b91c1c'; border = '1px solid #fecaca';
      } else if (['Rolling'].includes(type)) {
          bg = '#eff6ff'; color = '#1d4ed8'; border = '1px solid #bfdbfe';
      } else if (['Settle'].includes(type)) {
          bg = '#fff7ed'; color = '#c2410c'; border = '1px solid #fed7aa';
      } else if (['Share Profit', 'Rolling Commission', 'Rebate'].includes(type)) {
          bg = '#faf5ff'; color = '#7e22ce'; border = '1px solid #e9d5ff';
      }
      return (
          <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: '8px', backgroundColor: bg, color: color, border: border, fontSize: '12px', fontWeight: 'bold', minWidth: '80px', textAlign: 'center' }}>
              {type}
          </span>
      );
  };

  const getAgentIdByGame = (gameId) => {
      const game = games.find(g => g.id === gameId);
      return game ? game.agentId : '-';
  };

  const renderAgentCell = (agentCode) => (
      <td 
        onClick={() => handleAgentClick(agentCode)} 
        style={{ fontWeight: 'bold', color: '#2563eb', cursor: 'pointer', textDecoration:'underline' }}
        title="View Agent"
      >
          {agentCode}
      </td>
  );

  // 탭 메뉴 다국어 적용
  const tabs = [
    { id: 'game', label: t('tab_game') },
    { id: 'audit', label: t('tab_audit') },
    { id: 'account', label: t('tab_account') },
    { id: 'casino_chips', label: t('tab_chips') },
    { id: 'casino_cash', label: t('tab_cash') },
    { id: 'commission', label: t('rollingComm') + ' Out' },
    { id: 'share_profit', label: t('shareProfit') + ' Out' },
    { id: 'rebate', label: 'Rebate Income' } 
  ];

  return (
    <div style={{ padding: '30px', color: '#1e293b' }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'20px' }}>
        <h2>{t('menu_transactions')}</h2>
        <button onClick={()=>window.print()} style={{background:'#dc2626', color:'white', padding:'10px 20px', borderRadius:'8px', border:'none', fontWeight:'bold', display:'flex', alignItems:'center', gap:'5px', cursor:'pointer'}}><FileText size={18}/> {t('pdf')}</button>
      </div>

      <div style={{ display:'flex', gap:'10px', marginBottom:'20px', flexWrap:'wrap' }}>
        {tabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchTerm(''); }} 
                style={{
                    padding:'10px 20px', 
                    background:activeTab===tab.id?'#0f172a':'#e2e8f0', 
                    color:activeTab===tab.id?'white':'#64748b', 
                    border:'none', borderRadius:'20px', fontWeight:'bold', cursor:'pointer'
                }}>
                {tab.label}
            </button>
        ))}
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #ccc', borderRadius: '6px', padding: '5px 10px' }}>
              <Calendar size={16} color="#64748b" style={{ marginRight: '8px' }} />
              <input type="datetime-local" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: '#334155', cursor:'pointer', fontFamily: 'inherit' }} />
              <span style={{ margin: '0 8px', color: '#64748b' }}>~</span>
              <input type="datetime-local" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: '13px', color: '#334155', cursor:'pointer', fontFamily: 'inherit' }} />
          </div>
          <button onClick={() => { setStartDate(''); setEndDate(''); setSearchTerm(''); }} title="Reset" style={{ padding: '8px', background: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <RotateCcw size={16} color="#475569" />
          </button>
          <div style={{ position: 'relative', flex: 1 }}>
              <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder={t('search')} style={{ padding:'10px 10px 10px 35px', width:'100%', border:'1px solid #ccc', borderRadius:'6px' }} />
              <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          </div>
      </div>

      <div style={{ overflowX:'auto', background:'white', padding:'10px', borderRadius:'8px', border:'1px solid #e2e8f0' }}>
        
        {/* 1. Game Tab (Use currentItems) */}
        {activeTab === 'game' && (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'14px', textAlign:'center'}}>
                <thead><tr style={{background:'#f8fafc', borderBottom:'1px solid #e2e8f0'}}>
                    <th style={{padding:'12px'}}>{t('date')}</th><th>Game ID</th><th>{t('menu_agents')}</th><th>{t('type')}</th><th style={{textAlign:'right'}}>{t('amount')}</th><th style={{textAlign:'left', paddingLeft:'20px'}}>{t('remark')}</th><th>{t('staff')}</th>
                </tr></thead>
                <tbody>
                {currentItems.length > 0 ? currentItems.map((tx,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'12px', color:'#64748b'}}>{new Date(tx.date).toLocaleString()}</td>
                        <td style={{fontWeight:'bold'}}>{tx.gameId}</td>
                        {renderAgentCell(getAgentIdByGame(tx.gameId))}
                        <td>{renderTypeBadge(tx.type)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold'}}>{fmt(tx.amount)}</td>
                        <td style={{textAlign:'left', paddingLeft:'20px', color:'#334155'}}>{renderRemark(tx)}</td>
                        <td>{tx.staff}</td>
                    </tr>
                )) : <tr><td colSpan="7" style={{padding:'30px', color:'#94a3b8'}}>No Data</td></tr>}
                </tbody>
            </table>
        )}
        
        {/* 2. Audit Tab (Use currentItems) */}
        {activeTab === 'audit' && (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'14px', textAlign:'center'}}>
                <thead><tr style={{background:'#f1f5f9', borderBottom:'1px solid #e2e8f0'}}>
                    <th style={{padding:'12px'}}>{t('date')}</th><th>{t('type')}</th><th>Target ID</th><th style={{textAlign:'left'}}>Details</th><th>{t('staff')}</th>
                </tr></thead>
                <tbody>{currentItems.length > 0 ? currentItems.map((log,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'12px', color:'#64748b'}}>{new Date(log.date).toLocaleString()}</td>
                        <td style={{fontWeight:'bold'}}>{formatLogType(log.type)}</td>
                        <td style={{color:'#2563eb'}}>{log.targetId}</td>
                        <td style={{textAlign:'left', color:'#334155'}}>{getChangeDetails(log)}</td>
                        <td>{log.staff}</td>
                    </tr>
                )) : <tr><td colSpan="5" style={{padding:'30px', color:'#94a3b8'}}>No Data</td></tr>}</tbody>
            </table>
        )}

        {/* 3. Account Tab (Use currentItems) */}
        {activeTab === 'account' && (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'14px', textAlign:'center'}}>
                <thead><tr style={{background:'#f8fafc', borderBottom:'1px solid #e2e8f0'}}><th style={{padding:'12px'}}>{t('date')}</th><th>{t('menu_agents')}</th><th>Game ID</th><th>{t('type')}</th><th style={{textAlign:'right'}}>{t('amount')}</th><th style={{textAlign:'right'}}>{t('agent_balance')}</th><th style={{textAlign:'left', paddingLeft:'20px'}}>{t('remark')}</th><th>{t('staff')}</th></tr></thead>
                <tbody>{currentItems.length > 0 ? currentItems.map((tx,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'12px', color:'#64748b'}}>{new Date(tx.date).toLocaleString()}</td>
                        {renderAgentCell(tx.agentCode)}
                        <td>{tx.gameId||'-'}</td>
                        <td>{renderTypeBadge(tx.type)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold', color:['Deposit','Return','Game Win','Chips Out', 'Rebate'].includes(tx.type)?'#16a34a':'#dc2626'}}>{fmt(tx.amount)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold'}}>{fmt(tx.balance)}</td>
                        <td style={{textAlign:'left', paddingLeft:'20px', color:'#64748b'}}>{tx.remark}</td>
                        <td>{tx.staff}</td>
                    </tr>
                )) : <tr><td colSpan="8" style={{padding:'30px', color:'#94a3b8'}}>No Data</td></tr>}</tbody>
            </table>
        )}
        
        {/* 4. Casino Chips Tab (Use currentItems) */}
        {activeTab === 'casino_chips' && (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'14px', textAlign:'center'}}>
                <thead><tr style={{background:'#fff7ed', borderBottom:'1px solid #fed7aa'}}>
                    <th style={{padding:'12px'}}>{t('date')}</th><th>Game ID</th><th>{t('menu_agents')}</th><th>{t('action')}</th><th style={{textAlign:'right'}}>{t('amount')}</th><th style={{textAlign:'left', paddingLeft:'20px'}}>{t('remark')}</th><th>{t('staff')}</th></tr></thead>
                <tbody>{currentItems.length > 0 ? currentItems.map((tx,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'12px', color:'#64748b'}}>{new Date(tx.date).toLocaleString()}</td>
                        <td style={{fontWeight:'bold'}}>{tx.gameId || '-'}</td>
                        {renderAgentCell(tx.agentCode)}
                        <td>{renderTypeBadge(tx.type)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold'}}>{fmt(tx.amount)}</td>
                        <td style={{textAlign:'left', paddingLeft:'20px', color:'#334155'}}>{renderRemark(tx)}</td>
                        <td>{tx.staff}</td>
                    </tr>
                )) : <tr><td colSpan="7" style={{padding:'30px', color:'#94a3b8'}}>No Data</td></tr>}</tbody>
            </table>
        )}

        {/* 5. Casino Cash Tab (Use currentItems) */}
        {activeTab === 'casino_cash' && (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'14px', textAlign:'center'}}>
                <thead><tr style={{background:'#f0fdf4', borderBottom:'1px solid #bbf7d0'}}><th style={{padding:'12px'}}>{t('date')}</th><th>{t('menu_agents')}</th><th>Game ID</th><th>{t('action')}</th><th style={{textAlign:'right'}}>{t('amount')}</th><th>{t('remark')}</th><th>{t('staff')}</th></tr></thead>
                <tbody>{currentItems.length > 0 ? currentItems.map((tx,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'12px', color:'#64748b'}}>{new Date(tx.date).toLocaleString()}</td>
                        {renderAgentCell(tx.agentCode)}
                        <td>{tx.gameId||'-'}</td>
                        <td>{renderTypeBadge(tx.type)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold'}}>{fmt(tx.amount)}</td>
                        <td style={{color:'#64748b'}}>{tx.remark}</td>
                        <td>{tx.staff}</td>
                    </tr>
                )) : <tr><td colSpan="7" style={{padding:'30px', color:'#94a3b8'}}>No Data</td></tr>}</tbody>
            </table>
        )}

        {/* 6. Commission Tab (Use currentItems) */}
        {activeTab === 'commission' && (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'14px', textAlign:'center'}}>
                <thead><tr style={{background:'#faf5ff', borderBottom:'1px solid #e9d5ff'}}><th style={{padding:'12px'}}>{t('date')}</th><th>{t('menu_agents')}</th><th>{t('type')}</th><th style={{textAlign:'right'}}>{t('amount')}</th><th style={{textAlign:'right'}}>{t('agent_balance')}</th><th style={{textAlign:'left', paddingLeft:'20px'}}>{t('remark')}</th><th>{t('staff')}</th></tr></thead>
                <tbody>{currentItems.length > 0 ? currentItems.map((tx,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'12px', color:'#64748b'}}>{new Date(tx.date).toLocaleString()}</td>
                        {renderAgentCell(tx.agentCode)}
                        <td>{renderTypeBadge(tx.type)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold', color:'#dc2626'}}>{fmt(tx.amount)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold'}}>{fmt(tx.balance)}</td>
                        <td style={{textAlign:'left', paddingLeft:'20px', color:'#64748b'}}>{tx.remark}</td>
                        <td>{tx.staff}</td>
                    </tr>
                )) : <tr><td colSpan="7" style={{padding:'30px', color:'#94a3b8'}}>No Data</td></tr>}</tbody>
                
                {commissionTxs.length > 0 && (
                    <tfoot style={{borderTop:'2px solid #d8b4fe'}}>
                        <tr style={{background:'#faf5ff', fontWeight:'bold'}}>
                            <td colSpan="3" style={{padding:'12px', textAlign:'right', color:'#6b21a8'}}>
                                <div style={{display:'flex', alignItems:'center', justifyContent:'flex-end', gap:'5px'}}>
                                    <Calculator size={16}/> Total Commission Paid:
                                </div>
                            </td>
                            <td style={{textAlign:'right', fontSize:'15px', color:'#dc2626'}}>
                                {fmt(commissionTotal)}
                            </td>
                            <td colSpan="3"></td>
                        </tr>
                    </tfoot>
                )}
            </table>
        )}

        {/* 7. Share Profit Tab (Use currentItems) */}
        {activeTab === 'share_profit' && (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'14px', textAlign:'center'}}>
                <thead><tr style={{background:'#fdf4ff', borderBottom:'1px solid #fbcfe8'}}><th style={{padding:'12px'}}>{t('date')}</th><th>{t('menu_agents')}</th><th>{t('type')}</th><th style={{textAlign:'right'}}>{t('amount')}</th><th style={{textAlign:'right'}}>{t('agent_balance')}</th><th style={{textAlign:'left', paddingLeft:'20px'}}>{t('remark')}</th><th>{t('staff')}</th></tr></thead>
                <tbody>{currentItems.length > 0 ? currentItems.map((tx,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'12px', color:'#64748b'}}>{new Date(tx.date).toLocaleString()}</td>
                        {renderAgentCell(tx.agentCode)}
                        <td>{renderTypeBadge(tx.type)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold', color:'#dc2626'}}>{fmt(tx.amount)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold'}}>{fmt(tx.balance)}</td>
                        <td style={{textAlign:'left', paddingLeft:'20px', color:'#64748b'}}>{tx.remark}</td>
                        <td>{tx.staff}</td>
                    </tr>
                )) : <tr><td colSpan="7" style={{padding:'30px', color:'#94a3b8'}}>No Data</td></tr>}</tbody>
                
                {shareProfitTxs.length > 0 && (
                    <tfoot style={{borderTop:'2px solid #f9a8d4'}}>
                        <tr style={{background:'#fdf4ff', fontWeight:'bold'}}>
                            <td colSpan="3" style={{padding:'12px', textAlign:'right', color:'#be185d'}}>
                                <div style={{display:'flex', alignItems:'center', justifyContent:'flex-end', gap:'5px'}}>
                                    <Calculator size={16}/> Total Share Profit Paid:
                                </div>
                            </td>
                            <td style={{textAlign:'right', fontSize:'15px', color:'#dc2626'}}>
                                {fmt(shareProfitTotal)}
                            </td>
                            <td colSpan="3"></td>
                        </tr>
                    </tfoot>
                )}
            </table>
        )}

        {/* 8. Rebate Tab (Use currentItems) */}
        {activeTab === 'rebate' && (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:'14px', textAlign:'center'}}>
                <thead><tr style={{background:'#fefce8', borderBottom:'1px solid #fef08a'}}><th style={{padding:'12px'}}>{t('date')}</th><th>{t('menu_agents')}</th><th>{t('type')}</th><th style={{textAlign:'right'}}>{t('amount')}</th><th style={{textAlign:'right'}}>{t('agent_balance')}</th><th style={{textAlign:'left', paddingLeft:'20px'}}>{t('remark')}</th><th>{t('staff')}</th></tr></thead>
                <tbody>{currentItems.length > 0 ? currentItems.map((tx,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'12px', color:'#64748b'}}>{new Date(tx.date).toLocaleString()}</td>
                        {renderAgentCell(tx.agentCode)}
                        <td>{renderTypeBadge(tx.type)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold', color:'#dc2626'}}>{fmt(tx.amount)}</td>
                        <td style={{textAlign:'right', fontWeight:'bold'}}>{fmt(tx.balance)}</td>
                        <td style={{textAlign:'left', paddingLeft:'20px', color:'#64748b'}}>{tx.remark}</td>
                        <td>{tx.staff}</td>
                    </tr>
                )) : <tr><td colSpan="7" style={{padding:'30px', color:'#94a3b8'}}>No Data</td></tr>}</tbody>
                
                {rebateTxs.length > 0 && (
                    <tfoot style={{borderTop:'2px solid #fde047'}}>
                        <tr style={{background:'#fefce8', fontWeight:'bold'}}>
                            <td colSpan="3" style={{padding:'12px', textAlign:'right', color:'#854d0e'}}>
                                <div style={{display:'flex', alignItems:'center', justifyContent:'flex-end', gap:'5px'}}>
                                    <Calculator size={16}/> Total Rebate Paid:
                                </div>
                            </td>
                            <td style={{textAlign:'right', fontSize:'15px', color:'#dc2626'}}>
                                {fmt(rebateTotal)}
                            </td>
                            <td colSpan="3"></td>
                        </tr>
                    </tfoot>
                )}
            </table>
        )}

      </div>

      {/* ★ [추가] 페이지네이션 컨트롤 */}
      {totalPages > 1 && (
        <div className="no-print" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
            <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
                <ChevronLeft size={20} />
            </button>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#334155' }}>
                Page {currentPage} of {totalPages}
            </span>
            <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
                <ChevronRight size={20} />
            </button>
        </div>
      )}
    </div>
  );
};
export default TransactionHistory;